import React from 'react';
import { Target, TrendingUp, BarChart, PenTool } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../i18n';

const iconMap = {
  target: Target,
  'trending-up': TrendingUp,
  'bar-chart': BarChart,
  'pen-tool': PenTool
};

const Services = () => {
  const { t } = useLanguage();

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#E9782A] font-semibold text-sm uppercase tracking-wider">
            {t.services.label}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            {t.services.titlePre}{' '}
            <span className="text-[#E9782A]">{t.services.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-600">
            {t.services.subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {t.services.items.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <Card
                key={service.title}
                className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#E9782A]/20 bg-white animate-in fade-in zoom-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#E9782A]/10 flex items-center justify-center group-hover:bg-[#E9782A] transition-colors duration-300">
                      <Icon className="text-[#E9782A] group-hover:text-white transition-colors duration-300" size={28} />
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#E9782A] transition-colors duration-300">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="mt-6 flex items-center text-[#E9782A] font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
                    <span>{t.services.learnMore}</span>
                    <svg
                      className="ml-2 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#E9782A]/10 px-6 py-3 rounded-full">
            <span className="text-gray-700 font-medium">
              {t.services.ctaQuestion}
            </span>
            <a
              href="#contact"
              className="text-[#E9782A] font-semibold hover:underline"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t.services.ctaLink}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
